import {
  addEnglishTestScore,
  deleteEnglishTestScore,
  updateEnglishTestScore
} from '@crm/services.api';
import {
  errorNotificationHandler,
  successNotificationHandler
} from '@moxie/shared';
import {
  IAddEnglishTestScore,
  IUpdateEnglishTestScore
} from '@shared-components/models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LANGUAGE_SCORE_RESPONSE } from '@moxie/constants';
import { crmErrorHandler } from 'libs/shared/src/crm-modules/shared';
import { useAppSelector } from '@crm/core';
import { RootState } from '@crm/src/core/store';

export const useIeltsMutation = () => {
  const queryClient = useQueryClient();

  const socket = useAppSelector((state) => state.socket.wss);
  const user = useAppSelector((state) => state.auth.user);
  const contactData = useAppSelector(
    (state: RootState) => state?.contact?.singleData
  );

  return useMutation({
    mutationFn: (body: IAddEnglishTestScore) => addEnglishTestScore({ ...body, parentId: contactData.id }),
    onSuccess: async ({ data }) => {
      successNotificationHandler(LANGUAGE_SCORE_RESPONSE.ADD_SUCCESS);
      await queryClient.invalidateQueries({
        queryKey: ['englishTestScore'],
      });

      socket?.emit('server::profile-updated', {
        activitiesTypeId: contactData.id,
        activitiesType: 'language-test',
        activitiesAction: 'created',
        userId: user?.id,
        contactId: contactData?.id,
        companyId: user?.companyId as string,
        data: { testName: 'IELTS' },
      });
    },
    onError: () => {
      errorNotificationHandler('Error adding IELTS score.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['ielts'] });
    },
  });
};

export const useIeltsUpdate = () => {
  const queryClient = useQueryClient();

  const socket = useAppSelector((state) => state.socket.wss);
  const user = useAppSelector((state) => state.auth.user);
  const contactData = useAppSelector(
    (state: RootState) => state?.contact?.singleData
  );

  return useMutation({
    mutationFn: (body: IUpdateEnglishTestScore) => updateEnglishTestScore({ ...body, contactId: contactData.id }),
    onSuccess: async ({ data }) => {
      successNotificationHandler(LANGUAGE_SCORE_RESPONSE.UPDATE_SUCCESS);
      queryClient.invalidateQueries({
        queryKey: ['englishTestScore'],
      });
      socket?.emit('server::profile-updated', {
        activitiesTypeId: contactData.id,
        activitiesType: 'language-test',
        activitiesAction: 'updated',
        userId: user?.id,
        contactId: contactData?.id,
        companyId: user?.companyId as string,
        data: { testName: 'IELTS' },
      });
    },
    onError: () => {
      errorNotificationHandler('Error updating IELTS score.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['updateIelts'] });
    },
  });
};

export const useIeltsDelete = () => {
  const queryClient = useQueryClient();

  const socket = useAppSelector((state) => state.socket.wss);
  const user = useAppSelector((state) => state.auth.user);
  const contactData = useAppSelector(
    (state: RootState) => state?.contact?.singleData
  );

  return useMutation({
    mutationFn: (id: string) => deleteEnglishTestScore(id),
    onSuccess: async ({ data }) => {
      successNotificationHandler(LANGUAGE_SCORE_RESPONSE.DELETE_SUCCESS);
      queryClient.invalidateQueries({
        queryKey: ['englishTestScore'],
      });

      socket?.emit('server::profile-updated', {
        activitiesTypeId: contactData.id,
        activitiesType: 'language-test',
        activitiesAction: 'deleted',
        userId: user?.id,
        contactId: contactData?.id,
        companyId: user?.companyId as string,
        data: { testName: 'IELTS' },
      });
    },
    onError: (error) => {
      crmErrorHandler(error);
      errorNotificationHandler('Error deleting IELTS score.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deleteIelts'] });
    },
  });
};
